import React, { useState, useCallback, useRef } from "react";
import { Buttons, HeaderButton } from "components";
import FilterAltOffRoundedIcon from "@mui/icons-material/FilterAltOffRounded";
import s from "styles/pages/Pages.module.scss";
import { Cached } from "@mui/icons-material";
import Filters from "components/Filters";
import { useFormik } from "formik";
import XGridServer from "components/UI/Grid/XGridServer";
import { Toaster } from "sonner";

import { useMonitorSSE } from "./hooks/useMonitorSSE";
import { getStreamHeaders, SSE_STREAM_URL, gateMonitorGet, gateDetalleGet } from "./services/registros";
import { tableTitles, mapRegistroToRow } from "./utils/helpers";
import { getFechaHoy } from "utils/date";
import { log } from "utils/logger";

export function MonitorContent({
    zonaDefault,
    accesoDefault,
    zonas,
    accesos,
    credentials,
    errorLugares,
    loadingLugares,
}) {
    const [rowSelected, setRowSelected] = useState({ status: false, row: {} });
    const [forceUpload, setForceUpload] = useState(false);
    const [accesosFiltrados, setAccesosFiltrados] = useState(() =>
        accesos
            .filter(a => a.gateZonaId === zonaDefault)
            .map(a => ({
                id: a.gateLugarId,
                valor: a.gateLugarId,
                texto: `${a.gateLugarCod} - ${a.gateLugarNombre}`,
            }))
    );
    const [resetTable, setResetTable] = useState(false);
    const [filtrosAplicados, setFiltrosAplicados] = useState({
        accesos: [accesoDefault],
        fecha: getFechaHoy(),
    });

    const skipResetRef = useRef(false);
    const currentPageRef = useRef(0);
    const updateAbortRef = useRef(null);

    const handleChangeZona = useCallback((zona) => {
        if (!zona) return;
        const zonaId = Number(zona.gateZonaId);

        const accesosDeLaZona = accesos
            .filter(a => a.gateZonaId === zonaId)
            .map(a => ({
                id: a.gateLugarId,
                valor: a.gateLugarId,
                texto: `${a.gateLugarCod} - ${a.gateLugarNombre}`,
            }));

        setAccesosFiltrados(accesosDeLaZona);

        const nuevoDefault = accesosDeLaZona[0]?.valor ?? "";
        formik.setFieldValue("accesos", nuevoDefault ? [nuevoDefault] : []);
        setFiltrosAplicados(prev => ({
            ...prev,
            accesos: nuevoDefault ? [nuevoDefault] : [],
        }));
        setResetTable(v => !v);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accesos]);

    const accesosALugares = useCallback((accesosFiltradosSeleccionados) =>
        accesosFiltrados
            .filter(a => accesosFiltradosSeleccionados.includes(a.valor))
            .map(a => a.id),
        [accesosFiltrados]
    );

    const getStreamBody = useCallback(() => ({
        lugares: accesosALugares(filtrosAplicados.accesos),
    }), [filtrosAplicados.accesos, accesosALugares]);

    const getStreamHeadersFn = useCallback(() =>
        getStreamHeaders(credentials),
        [credentials]
    );

    const fetchDetalle = useCallback((id) =>
        gateDetalleGet({ id, credentials }),
        [credentials]
    );

    const {
        isConnected,
        reconnectAttempt,
        hasGivenUp,
        reconnect,
        nuevosEventos,
        resetNuevosEventos,
    } = useMonitorSSE({
        url: SSE_STREAM_URL,
        getBody: getStreamBody,
        getHeaders: getStreamHeadersFn,
        getDetalle: fetchDetalle,
        subscriptionKey: [...filtrosAplicados.accesos].sort().join(","),
        onRefresh: () => {
            skipResetRef.current = true;
            setForceUpload(v => !v);
        },
        shouldRefresh: () => currentPageRef.current === 0 && filtrosAplicados.fecha === getFechaHoy(),
        throttleMs: 3000,
    });

    const updateList = useCallback(async (page, pageSize) => {
        if (updateAbortRef.current) updateAbortRef.current.abort();
        updateAbortRef.current = new AbortController();
        currentPageRef.current = page;

        if (page === 0) resetNuevosEventos();
        if (!skipResetRef.current) setRowSelected({ status: false, row: {} });
        skipResetRef.current = false;

        try {
            const { rows, total } = await gateMonitorGet({
                page: page + 1,
                pageSize,
                fecha: filtrosAplicados.fecha,
                gateLugarId: filtrosAplicados.accesos,
                credentials,
                signal: updateAbortRef.current.signal,
            });
            const mapped = rows.map((row, index) => mapRegistroToRow(row, index));
            return { rows: mapped, rowCount: total };
        } catch (err) {
            if (err.name === "AbortError") return { rows: [], rowCount: 0 };
            log.error("Error cargando registros:", err);
            return { rows: [], rowCount: 0 };
        }
    }, [resetNuevosEventos, filtrosAplicados.accesos, filtrosAplicados.fecha, credentials]);

    const formik = useFormik({
        initialValues: { fecha: getFechaHoy(), accesos: [accesoDefault], zona: zonaDefault },
        onSubmit: (values) => {
            if (values.accesos.length === 0) return;
            setFiltrosAplicados({ accesos: values.accesos, fecha: values.fecha });
            setResetTable(v => !v);
        },
    });

    const handleLimpiarFiltros = () => {
        const accesosDeZonaDefault = accesos
            .filter(a => a.gateZonaId === zonaDefault)
            .map(a => ({
                id: a.gateLugarId,
                valor: a.gateLugarId,
                texto: `${a.gateLugarCod} - ${a.gateLugarNombre}`,
            }));

        setAccesosFiltrados(accesosDeZonaDefault);
        formik.resetForm({
            values: { fecha: getFechaHoy(), accesos: [accesoDefault], zona: zonaDefault },
        });
        setFiltrosAplicados({ accesos: [accesoDefault], fecha: getFechaHoy() });
        setResetTable(v => !v);
    };

    return (
        <main className={s.main}>
            <Toaster position="top-right" expand={true} />
            <div className={s["filters-actions"]}>
                <div style={{ display: "flex", alignItems: "center" }}>
                    <Filters
                        formik={formik}
                        inputsRender={[
                            { type: "date", name: "fecha", label: "Fecha" },
                            {
                                type: "autocomplete",
                                name: "zona",
                                label: "Zona",
                                width: "225px",
                                options: zonas,
                                firstOptionKey: "gateZonaId",
                                firstOptionValue: "gateZonaId",
                                firstOptionName: "gateZonaNombre",
                                onChange: handleChangeZona,
                            },
                            {
                                type: "autocompletemultiple",
                                name: "accesos",
                                label: "Filtrar por Accesos",
                                placeholder: loadingLugares
                                    ? "Cargando accesos..."
                                    : errorLugares
                                        ? `⚠️ ${errorLugares}`
                                        : "Buscar accesos...",
                                options: accesosFiltrados,
                                firstOptionKey: "id",
                                firstOptionValue: "valor",
                                firstOptionName: "texto",
                                disabled: loadingLugares || !!errorLugares,
                            },
                        ]}
                    />
                    <HeaderButton
                        type="button"
                        text="Limpiar filtros"
                        onClick={handleLimpiarFiltros}
                    >
                        <FilterAltOffRoundedIcon />
                    </HeaderButton>
                    {errorLugares && (
                        <span style={{ color: "#d32f2f", fontSize: "0.8em", fontWeight: "500" }}>
                            {errorLugares}
                        </span>
                    )}
                    {formik.values.accesos.length === 0 && (
                        <span style={{ color: "#d32f2f", fontSize: "0.75em", marginTop: 4 }}>
                            Seleccioná al menos un acceso para filtrar
                        </span>
                    )}
                </div>

                {nuevosEventos > 0 && (
                    <button
                        type="button"
                        onClick={() => {
                            const hoy = getFechaHoy();
                            resetNuevosEventos();
                            formik.setFieldValue("fecha", hoy);
                            setFiltrosAplicados(prev => ({ ...prev, fecha: hoy }));
                            setResetTable(v => !v);
                        }}
                        style={{
                            marginRight: "1rem",
                            padding: "0.4rem 0.8rem",
                            background: "#29b6f6",
                            color: "white",
                            border: "none",
                            borderRadius: 4,
                            cursor: "pointer",
                            fontSize: "0.85em",
                            fontWeight: "bold",
                        }}
                    >
                        🔵 {nuevosEventos} {nuevosEventos === 1 ? "nuevo evento" : "nuevos eventos"}
                    </button>
                )}

                {hasGivenUp ? (
                    <button
                        type="button"
                        onClick={reconnect}
                        style={{
                            marginRight: "1rem",
                            padding: "0.4rem 0.8rem",
                            background: "#f44336",
                            color: "white",
                            border: "none",
                            borderRadius: 4,
                            cursor: "pointer",
                            fontSize: "0.85em",
                        }}// igual que antes
                    >
                        🔄 Reintentar conexión
                    </button>
                ) : (
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        padding: "4px 10px",
                        borderRadius: "16px",
                        // border: `1px solid ${isConnected ? "#15171657" : reconnectAttempt > 0 ? "#fcd34d" : "#fca5a5"}`,
                        fontSize: "0.82em",
                        fontWeight: 500,
                        color: "#374151",
                    }}>
                        <span className={isConnected ? s.pulso : undefined}
                            style={{
                                display: "inline-block",
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                backgroundColor: isConnected ? "#4caf50" : reconnectAttempt > 0 ? "#ff9800" : "#f44336",
                            }}
                        />
                        {isConnected
                            ? "En vivo"
                            : reconnectAttempt > 0
                                ? `Reconectando (${reconnectAttempt}/10)`
                                : "Desconectado"}
                    </div>
                )}

                <Buttons>
                    <HeaderButton
                        type="button"
                        text="Refrescar"
                        onClick={() => {
                            skipResetRef.current = true;
                            setForceUpload(v => !v);
                        }}
                    >
                        <Cached />
                    </HeaderButton>
                </Buttons>
            </div>

            <div className={s.container}>
                <XGridServer
                    titles={tableTitles}
                    updateList={updateList}
                    forceReset={resetTable}
                    forceUpload={forceUpload}
                    secondarySelected={rowSelected}
                    setSecondarySelected={setRowSelected}
                    autoRow={'auto'}
                />
            </div>
        </main>
    );
}